var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
/**
 * A textarea component that works with native browser forms.
 *
 * Uses ElementInternals for form association, allowing the component
 * to participate in form submission, validation, and reset.
 *
 * Native input and change events bubble up from the inner textarea element.
 *
 * @element kr-textarea-field
 */
export class KRTextareaField extends LitElement {
    constructor() {
        super();
        // The value the user last typed. While dirty, an external `value` set is reverted to this (see
        // willUpdate) so a parent re-binding from the server can't overwrite an unsaved edit.
        this._dirtyValue = '';
        /**
         * The textarea label text
         */
        this.label = '';
        /**
         * The textarea name for form submission
         */
        this.name = '';
        /**
         * The current value
         */
        this.value = '';
        /**
         * Placeholder text
         */
        this.placeholder = '';
        /**
         * Whether the field is required
         */
        this.required = false;
        /**
         * Whether the field is disabled
         */
        this.disabled = false;
        /**
         * Whether the field is readonly
         */
        this.readonly = false;
        /**
         * Number of visible text lines
         */
        this.rows = 3;
        /**
         * Autocomplete attribute value
         */
        this.autocomplete = '';
        /**
         * Helper text shown below the textarea
         */
        this.hint = '';
        /**
         * Whether the user has edited this field since it was last loaded or marked pristine. Set on
         * user input, cleared by markPristine(). Used by <kr-form> to collect only changed fields.
         */
        this.dirty = false;
        /**
         * Whether a <kr-form> save carrying this field's edit is in flight. Set by markSaving(), cleared
         * by markSaved()/markSaveFailed().
         */
        this.saving = false;
        this._touched = false;
        this._handleInvalid = (e) => {
            e.preventDefault();
            this._touched = true;
        };
        this._internals = this.attachInternals();
    }
    connectedCallback() {
        super.connectedCallback();
        // Marks this as a krubble form field so <kr-form> can find its controls (.kr-form-field).
        this.classList.add('kr-form-field');
        this.addEventListener('invalid', this._handleInvalid);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('invalid', this._handleInvalid);
    }
    firstUpdated() {
        this._updateValidity();
    }
    willUpdate(changedProperties) {
        // This field's own save-mode, else the enclosing <kr-form>'s. Unset either way means manual.
        let saveMode = this.saveMode;
        if (!saveMode) {
            saveMode = this.closest('kr-form')?.saveMode;
        }
        // While the user has an unsaved edit, an external `value` set (e.g. a parent re-binding from a
        // server response that predates the edit) would overwrite it. Revert to the user's value. The
        // field accepts external values again once the edit is saved (markSaving clears dirty) or reset.
        if (changedProperties.has('value') && this.dirty && this.value !== this._dirtyValue && saveMode === 'autosave') {
            this.value = this._dirtyValue;
        }
    }
    updated(changedProperties) {
        if (changedProperties.has('required') || changedProperties.has('value')) {
            this._updateValidity();
        }
    }
    // Form-associated custom element callbacks
    get form() {
        return this._internals.form;
    }
    get validity() {
        return this._internals.validity;
    }
    get validationMessage() {
        return this._internals.validationMessage;
    }
    get willValidate() {
        return this._internals.willValidate;
    }
    checkValidity() {
        return this._internals.checkValidity();
    }
    reportValidity() {
        return this._internals.reportValidity();
    }
    formResetCallback() {
        this.value = '';
        this._touched = false;
        this.dirty = false;
        this.saving = false;
        this._internals.setFormValue('');
        this._internals.setValidity({});
    }
    // Clears the dirty flag so the field is no longer reported as changed.
    markPristine() {
        this.dirty = false;
    }
    // <kr-form> calls this when a save carrying this field's edit starts. Clearing dirty here means a
    // further edit during the request is tracked as new and goes out in the next save.
    markSaving() {
        this.saving = true;
        this.dirty = false;
    }
    // <kr-form> calls this when the save succeeded. If the user edited mid-flight the field is dirty
    // again and stays that way.
    markSaved() {
        this.saving = false;
    }
    // <kr-form> calls this when the save failed. Restore dirty so the next change retries the edit.
    markSaveFailed() {
        this.saving = false;
        this.dirty = true;
        this._dirtyValue = this.value;
    }
    formStateRestoreCallback(state) {
        this.value = state;
    }
    // Public methods for programmatic control
    focus() {
        this._textarea?.focus();
    }
    blur() {
        this._textarea?.blur();
    }
    select() {
        this._textarea?.select();
    }
    _updateValidity() {
        if (this._textarea) {
            this._internals.setValidity(this._textarea.validity, this._textarea.validationMessage);
        }
    }
    _handleInput(e) {
        this.value = e.target.value;
        this.dirty = true;
        this._dirtyValue = this.value;
        this._internals.setFormValue(this.value);
        this._internals.setValidity(this._textarea.validity, this._textarea.validationMessage);
    }
    // The inner textarea's native `change` fires on commit (blur after an edit) but doesn't cross the
    // shadow boundary — re-dispatch a composed one so a wrapping <kr-form> hears it.
    _handleChange(e) {
        this.value = e.target.value;
        this._internals.setFormValue(this.value);
        this.dispatchEvent(new Event('change', {
            bubbles: true,
            composed: true,
        }));
    }
    _handleBlur() {
        this._touched = true;
        this._internals.setValidity(this._textarea.validity, this._textarea.validationMessage);
    }
    render() {
        let validationMessage = '';
        if (this._touched && this._textarea && !this._textarea.validity.valid) {
            validationMessage = this._textarea.validationMessage;
        }
        return html `
      <div class="wrapper">
        ${this.label
            ? html `
            <label for="textarea">
              ${this.label}
              ${this.required ? html `<span class="required" aria-hidden="true">*</span>` : nothing}
            </label>
          `
            : nothing}
        <textarea
          id="textarea"
          class=${classMap({
            'textarea--invalid': this._touched && this._textarea && !this._textarea.validity.valid
        })}
          name=${this.name}
          .value=${live(this.value)}
          placeholder=${this.placeholder}
          ?required=${this.required}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          rows=${this.rows}
          cols=${ifDefined(this.cols)}
          minlength=${ifDefined(this.minlength)}
          maxlength=${ifDefined(this.maxlength)}
          autocomplete=${ifDefined(this.autocomplete || undefined)}
          @input=${this._handleInput}
          @change=${this._handleChange}
          @blur=${this._handleBlur}
        ></textarea>
        ${validationMessage
            ? html `<div class="validation-message">${validationMessage}</div>`
            : this.hint
                ? html `<div class="hint">${this.hint}</div>`
                : nothing}
      </div>
    `;
    }
}
KRTextareaField.styles = css `
    :host {
      display: block;
      font-family: inherit;
    }

    .wrapper {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    label {
      font-size: 14px;
      font-weight: 500;
      color: black;
    }

    .required {
      color: var(--kr-textarea-field-required-color, #ef4444);
      margin-left: 0.125rem;
    }

    textarea {
      font-family: inherit;
      font-size: 14px;
      line-height: 1.5;
      padding: 10px 12px;
      border: 1px solid var(--kr-textarea-field-border-color, #0000003d);
      border-radius: 8px;
      background-color: var(--kr-textarea-field-bg, #fff);
      color: var(--kr-textarea-field-color, #111827);
      transition: border-color 0.2s, box-shadow 0.2s;
      min-height: 2.5rem;
      resize: vertical;
    }

    textarea:focus {
      outline: none;
      border-color: var(--kr-textarea-field-focus-border-color, #163052);
      box-shadow: 0 0 0 3px var(--kr-textarea-field-focus-ring-color, rgba(22, 48, 82, 0.1));
    }

    textarea:disabled {
      background-color: var(--kr-textarea-field-disabled-bg, #f3f4f6);
      color: var(--kr-textarea-field-disabled-color, #9ca3af);
      cursor: not-allowed;
    }

    textarea::placeholder {
      color: var(--kr-textarea-field-placeholder-color, #9ca3af);
    }

    textarea.textarea--invalid {
      border-color: var(--kr-textarea-field-error-border-color, #ef4444);
    }

    textarea.textarea--invalid:focus {
      box-shadow: 0 0 0 3px var(--kr-textarea-field-error-ring-color, rgba(239, 68, 68, 0.1));
    }

    .validation-message {
      font-size: 0.75rem;
      color: var(--kr-textarea-field-error-color, #ef4444);
    }

    .hint {
      font-size: 0.75rem;
      color: var(--kr-textarea-field-helper-color, #6b7280);
    }
  `;
// Enable form association
KRTextareaField.formAssociated = true;
__decorate([
    property({ type: String })
], KRTextareaField.prototype, "label", void 0);
__decorate([
    property({ type: String })
], KRTextareaField.prototype, "name", void 0);
__decorate([
    property({ type: String })
], KRTextareaField.prototype, "value", void 0);
__decorate([
    property({ type: String })
], KRTextareaField.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean })
], KRTextareaField.prototype, "required", void 0);
__decorate([
    property({ type: Boolean })
], KRTextareaField.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], KRTextareaField.prototype, "readonly", void 0);
__decorate([
    property({ type: Number })
], KRTextareaField.prototype, "rows", void 0);
__decorate([
    property({ type: Number })
], KRTextareaField.prototype, "cols", void 0);
__decorate([
    property({ type: Number })
], KRTextareaField.prototype, "minlength", void 0);
__decorate([
    property({ type: Number })
], KRTextareaField.prototype, "maxlength", void 0);
__decorate([
    property({ type: String })
], KRTextareaField.prototype, "autocomplete", void 0);
__decorate([
    property({ type: String })
], KRTextareaField.prototype, "hint", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRTextareaField.prototype, "dirty", void 0);
__decorate([
    property({ attribute: 'save-mode' })
], KRTextareaField.prototype, "saveMode", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRTextareaField.prototype, "saving", void 0);
__decorate([
    state()
], KRTextareaField.prototype, "_touched", void 0);
__decorate([
    query('textarea')
], KRTextareaField.prototype, "_textarea", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-textarea-field')) {
    customElements.define('kr-textarea-field', KRTextareaField);
}
//# sourceMappingURL=textarea-field.js.map